import { moneyTips } from '../data/moneyTips';

// ======================================================
// Pocket Accounting - Daily Tip Selector
// ======================================================
//
// Goals:
//
// - Same financial concept for Parent + Kid each day
// - Different wording for Parent + Kid
// - Refreshing does NOT change the tip
// - A new tip appears on a new calendar day
// - Categories do NOT repeat on consecutive days
// - No database
// - No API
// - No AI
// - Adding categories/tips requires no changes here
// ======================================================

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

// Fixed reference point used only for deterministic
// daily calculations.
const EPOCH_DAY = Math.floor(
  Date.UTC(2026, 0, 1) / ONE_DAY_MS
);

// ------------------------------------------------------
// Create a YYYY-MM-DD string using the user's LOCAL date.
//
// We intentionally do not use:
// new Date().toISOString().slice(0, 10)
//
// because toISOString() uses UTC and could switch the
// daily tip too early or too late for some users.
// ------------------------------------------------------

const getLocalDateKey = (date = new Date()) => {
  const year = date.getFullYear();

  const month = String(
    date.getMonth() + 1
  ).padStart(2, '0');

  const day = String(
    date.getDate()
  ).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

// ------------------------------------------------------
// Convert a local calendar date into a stable day number.
// Date.UTC prevents daylight-saving changes from
// affecting the number of days between dates.
// ------------------------------------------------------

const getDayNumber = (date = new Date()) => {
  const utcDate = Date.UTC(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );

  return Math.floor(
    utcDate / ONE_DAY_MS
  );
};

// ------------------------------------------------------
// Small deterministic string hash.
//
// This is NOT used for security.
// It simply converts text into a repeatable number.
// ------------------------------------------------------

const hashString = (value) => {
  let hash = 2166136261;

  for (
    let index = 0;
    index < value.length;
    index += 1
  ) {
    hash ^= value.charCodeAt(index);

    hash = Math.imul(
      hash,
      16777619
    );
  }

  return hash >>> 0;
};

// ------------------------------------------------------
// Select today's category.
//
// Instead of independently hashing each date, today's
// category is derived from yesterday's category plus a
// deterministic jump.
//
// Because the jump is always at least 1, the category
// can NEVER be identical on two consecutive days.
//
// The result still feels random because the jump changes
// based on each date.
// ------------------------------------------------------

const getCategoryIndex = (
  dayNumber,
  categoryCount
) => {
  if (categoryCount <= 1) {
    return 0;
  }

  let categoryIndex = 0;

  const daysFromEpoch =
    dayNumber - EPOCH_DAY;

  // Handle dates after the reference date.
  if (daysFromEpoch >= 0) {
    for (
      let offset = 1;
      offset <= daysFromEpoch;
      offset += 1
    ) {
      const seed =
        hashString(
          `pocket-category-${EPOCH_DAY + offset}`
        );

      const jump =
        1 +
        (seed %
          (categoryCount - 1));

      categoryIndex =
        (categoryIndex + jump) %
        categoryCount;
    }

    return categoryIndex;
  }

  // Handle dates before the reference date.
  for (
    let offset = 0;
    offset > daysFromEpoch;
    offset -= 1
  ) {
    const seed =
      hashString(
        `pocket-category-${EPOCH_DAY + offset}`
      );

    const jump =
      1 +
      (seed %
        (categoryCount - 1));

    categoryIndex =
      (
        categoryIndex -
        jump +
        categoryCount
      ) %
      categoryCount;
  }

  return categoryIndex;
};

// ------------------------------------------------------
// Main function used by dashboards.
// ------------------------------------------------------

export const getDailyTip = (
  audience,
  date = new Date()
) => {
  if (
    audience !== 'parent' &&
    audience !== 'kid'
  ) {
    throw new Error(
      'getDailyTip audience must be "parent" or "kid".'
    );
  }

  const categories =
    Object.keys(moneyTips);

  if (categories.length === 0) {
    return null;
  }

  const dayNumber =
    getDayNumber(date);

  const dateKey =
    getLocalDateKey(date);

  const categoryIndex =
    getCategoryIndex(
      dayNumber,
      categories.length
    );

  const categoryKey =
    categories[categoryIndex];

  const category =
    moneyTips[categoryKey];

  const audienceTips =
    category?.[audience] || [];

  if (audienceTips.length === 0) {
    return null;
  }

  // Parent and Kid use the SAME daily category,
  // but their individual tip selection is allowed
  // to differ.
  const tipSeed =
    hashString(
      `${dateKey}-${categoryKey}-${audience}`
    );

  const tip =
    audienceTips[
      tipSeed % audienceTips.length
    ];

  return {
    ...tip,
    categoryKey,
    categoryLabel:
      category.label ||
      categoryKey,
    categoryEmoji:
      category.emoji || '💡',
    dateKey,
  };
};

// ------------------------------------------------------
// Helper for displaying the proper type label.
// ------------------------------------------------------

export const getTipTypeLabel = (
  audience,
  type
) => {
  if (audience === 'parent') {
    switch (type) {
      case 'conversation':
        return 'Conversation Starter';

      case 'try':
        return 'Try This Today';

      default:
        return 'Parent Tip';
    }
  }

  switch (type) {
    case 'challenge':
      return 'Money Challenge';

    case 'question':
      return 'Money Question';

    default:
      return 'Money Tip';
  }
};

export default getDailyTip;
